/**
 * ttm-triple-squeeze-page.js
 *
 * TTM Triple Squeeze — coins whose last closed Weekly, Daily AND 4H bars are
 * all in a squeeze (Bollinger(20,2) fully inside Keltner(20,2)) at once.
 *
 * Unlike the other strategy pages this is a state, not a long/short signal —
 * a squeeze has no direction until it fires — so it doesn't use
 * StrategySignalsPage (which is built around long/short tabs and counts).
 * Each card shows the momentum oscillator per timeframe as a lean hint only.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/loader';
import ErrorBanner from '../components/error-banner';
import { formatPrice, formatPercentChange } from '../utils/format-helpers';
import { isServerApiAvailable, fetchTtmTripleSqueezeData } from '../services/server-api';
import './rsi-mtf-page.css';

const TITLE    = 'TTM Triple Squeeze';
const ICON     = '🧨';
const SUBTITLE = 'Weekly, Daily and 4H all in a squeeze at once · Bollinger(20,2) inside Keltner(20,2) · no direction until it fires';

const TF_LABELS = { weekly: 'Weekly', daily: 'Daily', fourHour: '4H' };

function SqueezeCard({ entry }) {
  const navigate = useNavigate();

  return (
    <div
      className="mtf-card squeeze"
      onClick={() => navigate(`/coin/${entry.id}`)}
      title="Click for details"
    >
      <div className="mtf-card-top">
        <div className="mtf-coin-identity">
          {entry.image && (
            <img src={entry.image} alt={entry.name} className="mtf-coin-logo"
              onError={e => (e.target.style.display = 'none')} />
          )}
          <div>
            <div className="mtf-coin-symbol">{entry.symbol?.toUpperCase()}</div>
            <div className="mtf-coin-name">{entry.name}</div>
          </div>
        </div>
        <div className="mtf-direction-badge squeeze">● SQUEEZE ×3</div>
      </div>

      <div className="mtf-indicators">
        {Object.entries(TF_LABELS).map(([tf, label]) => {
          const mom = entry.sqzMom?.[tf];
          return (
            <div className="mtf-ind-row" key={tf}>
              <span className="mtf-ind-label">{label} momentum</span>
              <span className={`mtf-ind-value ${mom == null ? '' : mom > 0 ? 'bull' : mom < 0 ? 'bear' : ''}`}>
                {mom != null ? mom.toFixed(4) : '—'}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mtf-card-footer">
        <div className="mtf-price-block">
          <span className="mtf-price">{formatPrice(entry.current_price)}</span>
          <span className={`mtf-change ${(entry.price_change_percentage_24h || 0) >= 0 ? 'positive' : 'negative'}`}>
            {formatPercentChange(entry.price_change_percentage_24h)}
          </span>
        </div>
        <div className="mtf-rank">#{entry.marketCapRank}</div>
      </div>
    </div>
  );
}

function TtmTripleSqueezePage() {
  const [entries, setEntries]         = useState([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!isServerApiAvailable()) {
        setError('This strategy requires the VPS server. Start the server and refresh.');
        return;
      }
      const data = await fetchTtmTripleSqueezeData();
      setEntries(data.tripleSqueeze || []);
      setLastUpdated(new Date(data.lastUpdated).getTime());
    } catch (err) {
      if (err.message === 'Data not yet available') {
        setError('Server is starting up. Please refresh in a moment.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (isLoading) {
    return (
      <div className="mtf-page">
        <div className="mtf-page-header">
          <h1 className="mtf-page-title">{ICON} {TITLE}</h1>
          <p className="mtf-page-subtitle">{SUBTITLE}</p>
        </div>
        <div className="mtf-loading-center">
          <Loader size="large" text="Loading signals…" />
        </div>
      </div>
    );
  }

  return (
    <div className="mtf-page">
      <div className="mtf-page-header">
        <div className="mtf-header-main">
          <h1 className="mtf-page-title">{ICON} {TITLE}</h1>
          <p className="mtf-page-subtitle">{SUBTITLE}</p>
        </div>
        <button className="mtf-refresh-btn" onClick={load}>↻ Refresh</button>
      </div>

      {error && (
        <ErrorBanner message={error} type="error" onDismiss={() => setError(null)} onRetry={load} />
      )}

      <div className="mtf-summary-bar">
        <div className="mtf-summary-stat">
          <span className="mtf-summary-number">{entries.length}</span>
          <span className="mtf-summary-label">In Triple Squeeze</span>
        </div>
        {lastUpdated && (
          <div className="mtf-summary-updated">
            Updated {new Date(lastUpdated).toLocaleTimeString()}
          </div>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="mtf-empty">
          <div className="mtf-empty-icon">🔍</div>
          <div className="mtf-empty-text">No coins in a triple squeeze right now.</div>
          <div className="mtf-empty-sub">Weekly, Daily and 4H must all be compressed at once — check back after the next hourly refresh.</div>
        </div>
      ) : (
        <div className="mtf-grid">
          {entries.map(e => <SqueezeCard key={e.id} entry={e} />)}
        </div>
      )}
    </div>
  );
}

export default TtmTripleSqueezePage;
